import { useCareContext } from "../../providers";
import { useNavigate, useOutletContext } from "react-router";
import { useFeatureFlag } from "../../providers/FeatureFlags";
import { LoadingBounce } from "@frontend/lyng/loading";
import {
  useAvailableUsersForChatQuery,
  usePrivateChannelCreateMutation,
} from "../../api/generated/graphql";
import { useChatContext } from "@frontend/lyng/comms/ChatWrapper";
import {
  CreateChatButton,
  ChannelList as LyngChannelList,
} from "@frontend/lyng/comms/V2";
import { ChatWrapperContext } from "./ChatWrapper";
import { useTranslate } from "@tolgee/react";
import { useCallback } from "react";

export const ChatList = () => {
  const { t } = useTranslate();
  const {
    state: { viewer },
  } = useCareContext();
  const { userDict, fetchUsers } = useOutletContext<ChatWrapperContext>();

  const ff_messaging = useFeatureFlag("Messaging");

  const navigate = useNavigate();

  const { chatClient } = useChatContext();

  const { data: availableUsersForChat, loading: loadingAvailableUsersForChat } =
    useAvailableUsersForChatQuery();
  const [privateChannelCreateMutation, { loading: loadingCreateChannel }] =
    usePrivateChannelCreateMutation();

  const startChannel = useCallback(
    async (users: { id: string }[]) => {
      const { data, errors } = await privateChannelCreateMutation({
        variables: { input: { members: users.map(({ id }) => id) } },
      });

      if (data) {
        navigate(`/chat/${data.privateChannelCreate.id}`);
        return true;
      } else {
        console.error("error creating chat: ", errors);
        return false;
      }
    },
    [privateChannelCreateMutation, navigate],
  );

  if (!ff_messaging) {
    navigate("/");

    return;
  }

  if (!viewer || !chatClient) {
    return (
      <div className="flex justify-center dark:bg-greyscale-900 m-4">
        <LoadingBounce size="m" />
      </div>
    );
  }

  return (
    <>
      <div className="absolute top-0 right-0 mt-12 mr-6 z-20">
        <CreateChatButton
          size="sm"
          className="rounded-2xl"
          text={t("comms.newChat")}
          sortingOptions={viewer.tenantSettings}
          availableUsersForChat={
            availableUsersForChat?.availableUsersForChat ?? []
          }
          loadingAvailableUsers={loadingAvailableUsersForChat}
          creatingNewChannel={loadingCreateChannel}
          onClick={startChannel}
          translationMap={{
            to: t("comms.to"),
            noUsersFound: t("comms.noUsersFound"),
            noUsersExist: t("comms.noUsersExist"),
            startChat: t("comms.startChat"),
          }}
          fetchUsers={fetchUsers}
          userDict={userDict}
        />
      </div>
      <LyngChannelList
        client={chatClient}
        userDict={userDict}
        fetchUsers={fetchUsers}
        userId={viewer.id}
        loading={false}
        onSelect={(id) => navigate(`/chat/${id}`)}
        dateSettings={viewer.tenantSettings}
      />
    </>
  );
};
